import { styled } from "goober";
import { useState } from "react";
import type { MonitoringUnitsWorkflowRow } from "../../../shared/monitoringWorkflowsReport";
import { formatTinesUnits } from "../../lib/formatTinesUnits";
import { useSpaceDisplayName } from "../../hooks/useSpaceDisplayName";
import { spaceIcon } from "../../lib/spaceIcon";
import { useMakeUrl, navigate, routes, useCurrentUrl } from "../../lib/urls";
import { Button } from "../Button";
import { Avatar } from "../Avatar";
import { Icon } from "../Icon";
import { IconBadge } from "../IconBadge";
import { ListTable } from "../ListTable";
import { Modal } from "../Modal";
import { Text } from "../Text";
import { WorkflowBadge } from "../WorkflowBadge";
import {
  DETAIL_MODAL_STYLE,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalHeaderRight,
  ModalTitle,
} from "./MonitoringDetailCard";
import { useMonitoringInteraction } from "./analytics";
import { ListSection, NameBody, NameCell } from "./monitoringList";
import { foldsPersonal, PERSONAL_BUCKET } from "./monitoringScope";
import { TinesUnitsTrend } from "./TinesUnitsTrend";
import { TinesUnitsBySpace } from "./TinesUnitsBySpace";
import { TinesUnitsForecastBar } from "./TinesUnitsForecastBar";
import { unitsMonthLabel } from "./tinesUnitsChartLayout";
import {
  buildTinesUnitsSpaceRows,
  type TinesUnitsSpaceRow,
} from "./tinesUnitsBreakdownRows";
import { useMonitoringScope } from "./useMonitoringScope";
import {
  useMonitoringSpaces,
  useMonitoringUnitsMonth,
  useMonitoringWorkflows,
} from "./useMonitoringWorkflows";

// Stable identity so ListTable's row memo isn't defeated each render.
const NUMERIC_COLS = ["total"] as const;

const ChartsBody = styled(ModalBody)`
  display: grid;
  grid-template-rows: repeat(2, minmax(0, 1fr));
  gap: 12px;
  container: units-charts / size;

  &:has(> [data-single-month]) {
    grid-template-rows: auto minmax(0, 1fr);
  }
`;

const FooterNote = styled("div")`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const plural = (n: number, noun: string) => `${n} ${noun}${n === 1 ? "" : "s"}`;

// The drill-in behind the Tines units gauge, as a cascade of stacked modals:
// spaces → (a team space) its workflows, or (Personal) the personal spaces →
// one personal space's workflows. Each level X-closes to reveal the one below.
export function TinesUnitsBreakdown() {
  const makeUrl = useMakeUrl();
  const { report } = useMonitoringWorkflows();
  const { spaces, ownerBySpaceId } = useMonitoringSpaces();
  const displayName = useSpaceDisplayName();
  const { scope, matchesSpace, personalIds } = useMonitoringScope(
    routes.monitoring.workflows
  );
  const { panel, space, pspace } = useCurrentUrl(routes.monitoring.workflows);
  const recordInteraction = useMonitoringInteraction();
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const currentPeriodStart = report.unitsTrend?.currentPeriodStart;
  const periodStart = selectedMonth ?? currentPeriodStart;
  const isCurrentMonth =
    periodStart == null || periodStart === currentPeriodStart;
  const selectMonth = (month: number) => {
    setSelectedMonth(month);
    recordInteraction("workflows.units_month_change", {
      value: new Date(month).toISOString().slice(0, 7),
    });
  };

  // The current month rides the main report; a past month loads on demand.
  const monthBreakdown = useMonitoringUnitsMonth(
    isCurrentMonth ? null : (periodStart ?? null)
  );
  const loadingMonth = !isCurrentMonth && monthBreakdown.loading;
  const bySpace =
    (isCurrentMonth ? report.unitsBySpace : monthBreakdown.unitsBySpace) ?? [];
  const byWorkflow =
    (isCurrentMonth
      ? report.unitsByWorkflow
      : monthBreakdown.unitsByWorkflow) ?? [];
  const periodLabel =
    isCurrentMonth || periodStart == null
      ? "this month"
      : `in ${unitsMonthLabel.format(periodStart)}`;
  const workflowsById = new Map(report.workflows.map((w) => [w.id, w]));
  const currentForecast = (forecast: number | null | undefined) =>
    isCurrentMonth ? (forecast ?? null) : null;

  const spacesOpen = panel === "units";
  const level2Open = spacesOpen && !!space;
  const inPersonalBucket = space === PERSONAL_BUCKET;
  const level3Open = level2Open && inPersonalBucket && !!pspace;

  const spaceObj = (id: string | null) =>
    id == null ? undefined : spaces.find((s) => s.id === id);
  const spaceLabel = (id: string | null) => {
    if (id == null) return "";
    const found = spaceObj(id);
    return found ? displayName(found) : id;
  };
  const toRow = (row: (typeof bySpace)[number]): TinesUnitsSpaceRow => ({
    key: row.spaceId,
    label: spaceLabel(row.spaceId),
    icon: spaceIcon(spaceObj(row.spaceId)?.mode ?? "private"),
    total: row.total,
    storage: row.storage,
    workflowCount: row.workflowCount,
    owner: ownerBySpaceId.get(row.spaceId) ?? null,
    forecast: currentForecast(row.forecast),
  });

  const allSpaceRows = bySpace.map(toRow);
  const { topRows, personalRows, personalTotal } = buildTinesUnitsSpaceRows(
    allSpaceRows,
    matchesSpace,
    personalIds,
    foldsPersonal(scope),
    {
      key: PERSONAL_BUCKET,
      label: "Personal",
      icon: "circle-user-round",
      total: 0,
      storage: 0,
      workflowCount: 0,
      owner: null,
    }
  );

  const workflowsForSpace = (spaceId: string | null) =>
    byWorkflow
      .filter((row) => row.spaceId === spaceId)
      .sort((a, b) => b.total - a.total);

  const teamSpaceId = inPersonalBucket ? null : (space ?? null);
  // A back-navigated URL can point `space` at a row folded away or out of scope
  // in topRows; fall back to its raw entry so the storage line and footer still
  // resolve instead of silently vanishing.
  const rawTeam =
    teamSpaceId != null
      ? bySpace.find((row) => row.spaceId === teamSpaceId)
      : undefined;
  const teamRow =
    topRows.find((row) => row.key === space) ??
    (rawTeam ? toRow(rawTeam) : undefined);
  const personalRow = personalRows.find((row) => row.key === pspace);

  const closeAll = () => {
    setSelectedMonth(null);
    navigate(
      routes.monitoring.workflows,
      { panel: "", space: "", pspace: "" },
      { replace: true }
    );
  };
  const closeLevel2 = () =>
    navigate(
      routes.monitoring.workflows,
      { space: "", pspace: "" },
      { replace: true }
    );
  const closeLevel3 = () =>
    navigate(routes.monitoring.workflows, { pspace: "" }, { replace: true });
  const spaceItems = (
    rows: TinesUnitsSpaceRow[],
    href: (key: string) => string
  ) =>
    rows.map((row) => ({
      key: row.key,
      link: href(row.key),
      onNavigate: () =>
        recordInteraction("workflows.panel_drilldown_open", {
          targetType: "space",
          targetId: row.key,
          value: "units",
        }),
      space: (
        <NameCell>
          {row.owner ? (
            <Avatar user={row.owner} size={32} />
          ) : (
            <IconBadge>
              <Icon name={row.icon} size={16} />
            </IconBadge>
          )}
          <NameBody>
            <Text variant="text-regular-md" ellipsis>
              {row.label}
            </Text>
            <Text variant="text-small" color="var(--text-secondary)">
              {plural(row.workflowCount, "workflow")}
            </Text>
          </NameBody>
        </NameCell>
      ),
      total: <Text variant="text-small-md">{formatTinesUnits(row.total)}</Text>,
    }));

  // A pinned, non-clickable line for the units that belong to the space itself
  // rather than any workflow: storage, plus runs like chat that have no workflow.
  const otherUsageItem = (
    units: number,
    forecast: number | null,
    max: number
  ) => ({
    key: "__other__",
    name: () => (
      <NameCell>
        <IconBadge>
          <Icon name="database" size={16} />
        </IconBadge>
        <NameBody>
          <Text variant="text-regular-md" ellipsis>
            Other usage
          </Text>
          <Text variant="text-small" color="var(--text-secondary)">
            Storage and runs outside workflows, like chat
          </Text>
        </NameBody>
      </NameCell>
    ),
    total:
      forecast != null ? (
        <TinesUnitsForecastBar used={units} forecast={forecast} max={max} />
      ) : (
        <Text variant="text-small-md">{formatTinesUnits(units)}</Text>
      ),
  });

  const workflowItems = (
    allRows: MonitoringUnitsWorkflowRow[],
    max: number
  ) => {
    const rows = allRows.map((row) => ({
      ...row,
      forecast: currentForecast(row.forecast),
    }));
    return rows.map((row) => {
      const wf = workflowsById.get(row.workflowId);
      return {
        key: row.workflowId,
        link: wf
          ? makeUrl(routes.space.workflow, {
              spaceId: wf.spaceId,
              workflowId: row.workflowId,
            })
          : undefined,
        name: (hover: boolean) => (
          <NameCell>
            <WorkflowBadge
              workflow={{
                id: row.workflowId,
                spaceId: wf?.spaceId,
                badgeSeed: wf?.badgeSeed ?? null,
                pausedAt: wf?.pausedAt ?? null,
                branches: [{ name: "main" }],
              }}
              hover={hover}
            />
            <NameBody>
              <Text variant="text-regular-md" ellipsis>
                {wf?.name ?? "Unknown workflow"}
              </Text>
            </NameBody>
          </NameCell>
        ),
        total:
          row.forecast != null ? (
            <TinesUnitsForecastBar
              used={row.total}
              forecast={row.forecast}
              max={max}
            />
          ) : (
            <Text variant="text-small-md">{formatTinesUnits(row.total)}</Text>
          ),
      };
    });
  };

  // The other-usage line first, then the workflows ranked by units, so the list
  // adds up to the space's header total. Every forecast bar is scaled to the
  // space's own month-end projection, so each row reads as its share of it.
  const spaceUsageItems = (
    row: TinesUnitsSpaceRow | undefined,
    spaceId: string | null
  ) => {
    const workflows = workflowsForSpace(spaceId);
    const spaceForecast = row?.forecast ?? null;
    const sum = (values: number[]) => values.reduce((a, b) => a + b, 0);
    const otherUsed = Math.max(
      0,
      (row?.total ?? 0) - sum(workflows.map((wf) => wf.total))
    );
    const otherForecast =
      spaceForecast == null
        ? null
        : Math.max(
            0,
            spaceForecast -
              sum(workflows.map((wf) => currentForecast(wf.forecast) ?? 0))
          );
    const max = spaceForecast ?? 1;
    // Whole units, so float leftovers from the subtraction don't add a row.
    const showOther = Math.round(Math.max(otherUsed, otherForecast ?? 0)) > 0;
    return [
      ...(showOther ? [otherUsageItem(otherUsed, otherForecast, max)] : []),
      ...workflowItems(workflows, max),
    ];
  };

  const unitsFooter = (total: number | undefined, forecast?: number | null) => (
    <FooterNote>
      <Text variant="text-small" color="var(--text-secondary)">
        {total != null && (
          <>
            {formatTinesUnits(total)} Tines units {periodLabel}
            {forecast != null &&
              `, ${formatTinesUnits(forecast)} projected by month end`}
          </>
        )}
      </Text>
    </FooterNote>
  );

  // A past month's breakdown loads on demand, so distinguish an empty month from
  // one still arriving.
  const emptyUsage = (message: string) => (
    <Text variant="text-regular" color="var(--text-secondary)">
      {loadingMonth ? "Loading…" : message}
    </Text>
  );

  return (
    <>
      {spacesOpen && (
        <Modal open onClose={closeAll} style={DETAIL_MODAL_STYLE}>
          <ModalHeader>
            <ModalTitle icon="circle-gauge" title="Tines units" />
            <ModalHeaderRight>
              <Button
                size="l"
                variant="secondary"
                icon="x"
                onClick={closeAll}
              />
            </ModalHeaderRight>
          </ModalHeader>
          <ChartsBody data-popover-host>
            {periodStart != null && (
              <>
                <TinesUnitsTrend
                  selectedPeriodStart={periodStart}
                  onSelectMonth={selectMonth}
                />
                <TinesUnitsBySpace
                  periodStart={periodStart}
                  onSelectMonth={selectMonth}
                  onOpenSpace={(spaceId) => {
                    recordInteraction("workflows.panel_drilldown_open", {
                      targetType: "space",
                      targetId: spaceId,
                      value: "units",
                    });
                    navigate(routes.monitoring.workflows, {
                      panel: "units",
                      space: spaceId,
                      pspace: "",
                    });
                  }}
                />
              </>
            )}
          </ChartsBody>
          <ModalFooter>
            <Button size="l" variant="primary" onClick={closeAll}>
              Done
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {/* Level 2 — a team space's usage, or the personal spaces */}
      <Modal open={level2Open} onClose={closeLevel2} style={DETAIL_MODAL_STYLE}>
        <ModalHeader>
          <ModalTitle
            icon="circle-gauge"
            title="Tines units"
            space={inPersonalBucket ? undefined : spaceObj(teamSpaceId)}
          />
          <ModalHeaderRight>
            <Button
              size="l"
              variant="secondary"
              icon="x"
              onClick={closeLevel2}
            />
          </ModalHeaderRight>
        </ModalHeader>
        <ModalBody style={{ gap: 0 }}>
          {inPersonalBucket ? (
            <ListSection
              title="Personal spaces"
              description={`${plural(personalRows.length, "personal space")}, ranked by Tines units. Select one to see its workflows.`}
            >
              <ListTable
                headers={{ space: "Space", total: "Tines units" }}
                columns={{ space: "flex", total: "md" }}
                numericColumns={NUMERIC_COLS}
                emptyState={emptyUsage(
                  `No personal-space usage ${periodLabel}.`
                )}
                items={spaceItems(personalRows, (key) =>
                  makeUrl(routes.monitoring.workflows, {
                    panel: "units",
                    space: PERSONAL_BUCKET,
                    pspace: key,
                  })
                )}
              />
            </ListSection>
          ) : (
            <ListSection title={`Usage in ${spaceLabel(teamSpaceId)}`}>
              <ListTable
                headers={{ name: "", total: "Tines units" }}
                columns={{ name: "flex", total: "md" }}
                numericColumns={NUMERIC_COLS}
                emptyState={emptyUsage(
                  `No usage attributed to this space ${periodLabel}.`
                )}
                items={spaceUsageItems(teamRow, teamSpaceId)}
              />
            </ListSection>
          )}
        </ModalBody>
        <ModalFooter>
          {unitsFooter(
            inPersonalBucket ? personalTotal : teamRow?.total,
            inPersonalBucket ? null : teamRow?.forecast
          )}
          <Button size="l" variant="primary" onClick={closeLevel2}>
            Done
          </Button>
        </ModalFooter>
      </Modal>

      {/* Level 3 — a personal space's usage */}
      <Modal open={level3Open} onClose={closeLevel3} style={DETAIL_MODAL_STYLE}>
        <ModalHeader>
          <ModalTitle
            icon="circle-gauge"
            title="Tines units"
            space={spaceObj(pspace ?? null)}
          />
          <ModalHeaderRight>
            <Button
              size="l"
              variant="secondary"
              icon="x"
              onClick={closeLevel3}
            />
          </ModalHeaderRight>
        </ModalHeader>
        <ModalBody style={{ gap: 0 }}>
          <ListSection title={`Usage in ${spaceLabel(pspace ?? null)}`}>
            <ListTable
              headers={{ name: "", total: "Tines units" }}
              columns={{ name: "flex", total: "md" }}
              numericColumns={NUMERIC_COLS}
              emptyState={emptyUsage(
                `No usage attributed to this space ${periodLabel}.`
              )}
              items={spaceUsageItems(personalRow, pspace ?? null)}
            />
          </ListSection>
        </ModalBody>
        <ModalFooter>
          {unitsFooter(personalRow?.total, personalRow?.forecast)}
          <Button size="l" variant="primary" onClick={closeLevel3}>
            Done
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
